import React from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingStatus from '@/components/ui/LoadingStatus';
import DelayedLoading from '@/components/ui/DelayedLoading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PlayerSelectGrid from '@/components/comparison/PlayerSelectGrid';
import { Player } from '@/types';
import {
  ANALYSIS_TYPES,
  TIME_RANGES,
  FORMATIONS,
  CONTEXT_TEMPLATES,
  AnalysisType,
} from './constants';

interface ConfigureAnalysisCardProps {
  selectedAnalysis: string;
  selectedAnalysisType: AnalysisType | undefined;
  timeRange: string;
  formation: string;
  userContext: string;
  loading: boolean;
  loadingPlayers: boolean;
  players: Player[];
  selectedPlayerIds: string[];
  onSelectAnalysis: (id: string) => void;
  onTimeRangeChange: (value: string) => void;
  onFormationChange: (value: string) => void;
  onUserContextChange: (value: string) => void;
  onTogglePlayer: (playerId: string) => void;
  onAnalyze: () => void;
}

const ConfigureAnalysisCard: React.FC<ConfigureAnalysisCardProps> = ({
  selectedAnalysis,
  selectedAnalysisType,
  timeRange,
  formation,
  userContext,
  loading,
  loadingPlayers,
  players,
  selectedPlayerIds,
  onSelectAnalysis,
  onTimeRangeChange,
  onFormationChange,
  onUserContextChange,
  onTogglePlayer,
  onAnalyze,
}) => {
  return (
    <Card className="space-y-6 !overflow-visible">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Configure Analysis</h2>
      </div>
      <div className="space-y-6">
        {/* Analysis Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ANALYSIS_TYPES.map((type) => {
            return (
              <div
                key={type.id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedAnalysis === type.id
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-400'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                }`}
                onClick={() => {
                  onSelectAnalysis(type.id);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectAnalysis(type.id); } }}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-lg">{type.icon}</span>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">{type.name}</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">{type.description}</p>
              </div>
            );
          })}
        </div>

        {/* Time Range Selection */}
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Time Range:</span>
          <Select value={timeRange} onValueChange={onTimeRangeChange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((range) => (
                <SelectItem key={range.id} value={range.id}>
                  {range.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Formation Selection (only for team composition) */}
        {selectedAnalysisType?.supportsFormation && (
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Formation:</span>
            <Select value={formation} onValueChange={onFormationChange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMATIONS.map((form) => (
                  <SelectItem key={form.id} value={form.id}>
                    {form.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Player Selection (only for analyses that require it) */}
        {selectedAnalysisType?.requiresPlayers && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Players to Analyze:
              </span>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {selectedPlayerIds.length} selected
              </div>
            </div>

            {loadingPlayers ? (
              <DelayedLoading loading><LoadingStatus label="Loading analysis players" className="justify-center p-8" /></DelayedLoading>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                <PlayerSelectGrid
                  players={players}
                  selectedIds={selectedPlayerIds}
                  toggleSelect={onTogglePlayer}
                />
              </div>
            )}

            {selectedPlayerIds.length > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Selected: {players.filter(p => selectedPlayerIds.includes(p.id)).map(p => p.name).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* User Context Input */}
        <div className="space-y-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Context & Specific Questions (Optional):
          </span>

          {/* Context Templates */}
          <div className="flex flex-wrap gap-2">
            {CONTEXT_TEMPLATES.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => onUserContextChange(template.text)}
                className="text-xs px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {template.text}
              </button>
            ))}
          </div>

          {/* Text Area */}
          <textarea
            value={userContext}
            onChange={(e) => onUserContextChange(e.target.value)}
            placeholder="Enter specific questions or focus areas for the analysis..."
            className="w-full h-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent resize-none"
            maxLength={500}
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
            {userContext.length}/500 characters
          </div>
        </div>

        {/* Action Button */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {selectedAnalysisType && (
              <span>Selected: {selectedAnalysisType.name}</span>
            )}
          </div>
          <Button
            onClick={onAnalyze}
            disabled={!selectedAnalysis || loading || (selectedAnalysisType?.requiresPlayers && selectedPlayerIds.length === 0)}
            loading={loading}
            loadingLabel="Analyzing players"
            className="min-w-32"
          >
            Analyze
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ConfigureAnalysisCard;
